import { Rng } from '@/core/rng';
// The rule is "out of sight", which is a fact about the screen.
import { viewHeight, viewWidth } from '@/core/viewport';
import type { EnemyId } from '@/data/enemies';
import { TILE_SIZE } from '@/sprites/tiles';
import type { Tilemap } from '@/world/tilemap';

/**
 * Where enemies come from, and when.
 *
 * A fixed table of spawn points was a placeholder: every visit to an area put
 * the same three creatures on the same three tiles, so a field was a diorama
 * rather than somewhere that might be dangerous.
 *
 * Enemies now arrive in waves, out of sight, in numbers that depend on the
 * hour. The rule is Terraria's and it is a good one — the world is a different
 * place after dark, and that is what makes going home at dusk a decision
 * rather than a habit.
 *
 * Nothing here is saved. Population is a property of being somewhere, not of
 * the save file, and an area emptied of enemies fills again by standing in it.
 */

/**
 * Spawns land outside the screen, and inside a band beyond it.
 *
 * Derived from the view rather than fixed, because the view is no longer
 * fixed: it fills the screen, so a wide phone sees more world sideways than a
 * square window does. A constant tuned for one shape lets things blink into
 * existence at the edge of another.
 *
 * Half the diagonal is the furthest corner of the screen; a tile past that is
 * out of sight in every direction.
 */
const SIGHT_MARGIN_TILES = 1;

/** How much further than "just out of sight" a spawn may be. */
const SPAWN_BAND_TILES = 10;

/** Never closer than this, however small the window gets. */
const MIN_SPAWN_FLOOR_TILES = 12;

function spawnFloorTiles(): number {
  const halfDiagonal = Math.hypot(viewWidth(), viewHeight()) / 2 / TILE_SIZE;
  return Math.max(MIN_SPAWN_FLOOR_TILES, Math.ceil(halfDiagonal) + SIGHT_MARGIN_TILES);
}

/** Candidate tiles tried per spawn before giving up for this wave. */
const PLACEMENT_ATTEMPTS = 24;

export interface AreaSpawnRules {
  /** What can appear here. Repeats weight a creature more heavily. */
  readonly table: readonly EnemyId[];
  /** Ceiling on live enemies during the day. */
  readonly dayCap: number;
  /** And after dark. Higher, or night means nothing. */
  readonly nightCap: number;
  /** Seconds between waves. */
  readonly intervalSeconds: number;
  /** Most that can arrive in one wave, so a quiet field does not fill at once. */
  readonly waveSize: number;
}

export interface SpawnContext {
  readonly map: Tilemap;
  /** Player centre, in world pixels. */
  readonly playerX: number;
  readonly playerY: number;
  readonly isNight: boolean;
  /** How many enemies are already alive here. */
  readonly population: number;
}

export interface Spawn {
  readonly id: EnemyId;
  /** World pixels, centred on the tile. */
  readonly x: number;
  readonly y: number;
}

/** Night is dusk to dawn: when the ambient tint is at its deepest. */
export function isNightHour(hour: number): boolean {
  return hour >= 19 || hour < 5;
}

export class SpawnDirector {
  private elapsed = 0;
  private wave: number;

  constructor(
    private readonly seed: number,
    private readonly areaSalt: number,
    private readonly rules: AreaSpawnRules,
    /**
     * Where to resume the wave sequence.
     *
     * A director is rebuilt whenever an area is entered, and starting from
     * zero each time meant the first wave of every visit was identical — the
     * same creature on the same tile, which is the fixed table this replaced
     * wearing a disguise. The count carries across areas so leaving and
     * coming back is genuinely a different place.
     */
    startWave = 0,
  ) {
    this.wave = startWave;
  }

  /** So the next director can pick up where this one left off. */
  get wavesSent(): number {
    return this.wave;
  }

  /** Bring the next wave forward, so entering an area is not silent. */
  primeImmediately(): void {
    this.elapsed = this.rules.intervalSeconds;
  }

  /**
   * Advance the clock and return what should appear, if anything.
   *
   * Returns spawns rather than creating them: where enemies come from is a
   * decision, and making it separately from the entity store is what lets
   * "does night bring more of them" be a question about numbers.
   */
  update(dt: number, context: SpawnContext): Spawn[] {
    this.elapsed += dt;
    if (this.elapsed < this.rules.intervalSeconds) return [];
    this.elapsed = 0;

    const cap = context.isNight ? this.rules.nightCap : this.rules.dayCap;
    const room = cap - context.population;
    if (room <= 0) return [];

    // A fresh stream per wave, so consecutive waves share nothing and the
    // same field is never laid out twice.
    const rng = Rng.forStream(this.seed, this.wave++, this.areaSalt);

    // Random, not the maximum: a wave that always arrives at full strength is
    // as predictable as a fixed table, just busier.
    const wanted = Math.min(room, rng.int(1, this.rules.waveSize));
    const spawns: Spawn[] = [];

    for (let i = 0; i < wanted; i++) {
      const place = this.findPlace(rng, context);
      if (!place) break;

      const id = rng.pick(this.rules.table);
      if (!id) break;

      spawns.push({ id, x: place.x, y: place.y });
    }

    return spawns;
  }

  /**
   * A walkable tile in the ring around the player.
   *
   * Rejection sampling rather than scanning every tile in range: a hit is
   * likely within a few tries in open country, and giving up is the correct
   * answer in a corridor where there is genuinely nowhere out of sight.
   */
  private findPlace(rng: Rng, context: SpawnContext): { x: number; y: number } | null {
    const playerTileX = Math.floor(context.playerX / TILE_SIZE);
    const playerTileY = Math.floor(context.playerY / TILE_SIZE);

    for (let attempt = 0; attempt < PLACEMENT_ATTEMPTS; attempt++) {
      const angle = rng.next() * Math.PI * 2;
      const floor = spawnFloorTiles();
      const distance = floor + rng.next() * SPAWN_BAND_TILES;

      const tileX = Math.round(playerTileX + Math.cos(angle) * distance);
      const tileY = Math.round(playerTileY + Math.sin(angle) * distance);

      if (context.map.isSolidTile(tileX, tileY)) continue;

      // Guard the bounds explicitly: isSolidTile reports out-of-map as solid
      // today, and a spawner that silently depends on that is a trap for
      // whoever changes it.
      if (tileX < 0 || tileY < 0) continue;
      if (tileX >= context.map.width || tileY >= context.map.height) continue;

      return { x: tileX * TILE_SIZE + TILE_SIZE / 2, y: tileY * TILE_SIZE + TILE_SIZE / 2 };
    }

    return null;
  }
}
